// Oracle CREATE TRIGGER target-table extractor.
//
// Walks the buffer up to the cursor and returns the target table of the
// nearest enclosing `CREATE [OR REPLACE] TRIGGER <name> … ON [<schema>.]<table>`
// statement. Used by `emitDottedAccess` in `oracleResolver.ts` so that
// `:NEW.|<col>` / `:OLD.|<col>` inside the trigger body resolves to the
// trigger's target-table columns.
//
// The shared `triggerHostTable.ts` helper deliberately excludes Oracle
// because Oracle uses the `:NEW` / `:OLD` colon-prefixed bind syntax —
// pseudo-relation dispatch needs to inspect the dottedBase AND the colon
// prefix at the resolver layer rather than relying on the shared helper's
// engine-uniform path.
//
// Approach: text-walk via the shared tokenizer. The trigger header runs
//   CREATE [OR REPLACE] TRIGGER <name> {BEFORE|AFTER|INSTEAD OF}
//     {INSERT|UPDATE|DELETE} [OR …] [OF <col-list>] ON [<schema>.]<table>
// We search for `CREATE` followed (within a small window) by `TRIGGER`,
// then scan ahead at depth 0 for the next `ON` keyword whose RHS is an
// identifier (optionally schema-qualified). That identifier is the target
// table. The most recent CREATE TRIGGER whose ON clause precedes the
// cursor wins.

import { skipWhitespaceAndCommentsBackward } from '../context/skipCommentsBackward';
import { tokenize, type Token } from '../context/tokenizer';

const ENGINE = 'oracle' as const;

export type OracleTriggerHostTable = {
  readonly name: string;
  readonly schema: string | undefined;
};

const upperOf = (t: Token | undefined): string | null => {
  if (!t) return null;
  if (t.type !== 'ident' && t.type !== 'kw') return null;
  return t.text.toUpperCase();
};

/**
 * True iff the text immediately before the dotted-base identifier at
 * `cursorOffset` is a colon — i.e. the source has the shape `:<base>.|…`.
 * Used to discriminate Oracle's `:NEW` / `:OLD` trigger pseudo-relation
 * references from a bare `NEW` / `OLD` identifier that would land in this
 * branch too (e.g. an unrelated alias).
 *
 * The walk is intentionally tiny: from the cursor, step back past any
 * trailing word chars (in-progress identifier), then past the `.`, then
 * past the base identifier, then past whitespace, and look at the prior
 * char. Whitespace skips also consume trailing block comments so a
 * `:NEW.<comment>col` shape still binds (the comment is whitespace-
 * equivalent). Caller supplies the expected uppercased base (`NEW` or
 * `OLD`) so we can early-out if the identifier we walked over doesn't match.
 */
export function isColonPrefixedAtCursor(
  source: string,
  cursorOffset: number,
  expectedBaseUpper: string,
): boolean {
  let pos = Math.min(cursorOffset, source.length);
  while (pos > 0 && isBareIdentChar(source.charCodeAt(pos - 1))) pos -= 1;
  pos = skipWhitespaceAndCommentsBackward(source, pos, 0);
  if (pos === 0 || source.charCodeAt(pos - 1) !== 0x2e /* . */) return false;
  pos = skipWhitespaceAndCommentsBackward(source, pos - 1, 0);

  const baseEnd = pos;
  while (pos > 0 && isBareIdentChar(source.charCodeAt(pos - 1))) pos -= 1;
  const baseText = source.slice(pos, baseEnd);
  if (baseText.toUpperCase() !== expectedBaseUpper) return false;

  pos = skipWhitespaceAndCommentsBackward(source, pos, 0);
  return pos > 0 && source.charCodeAt(pos - 1) === 0x3a /* : */;
}

function isBareIdentChar(c: number): boolean {
  return (
    (c >= 0x30 && c <= 0x39) ||
    (c >= 0x41 && c <= 0x5a) ||
    (c >= 0x61 && c <= 0x7a) ||
    c === 0x5f
  );
}

/**
 * Returns the target table of the latest `CREATE [OR REPLACE] TRIGGER …
 * ON <table>` statement whose ON clause precedes `cursorOffset`. Returns
 * `undefined` when no qualifying trigger appears before the cursor, or
 * when the header is incomplete (cursor sits between TRIGGER and ON, or
 * before the ON keyword).
 */
export function findOracleTriggerHostTable(
  source: string,
  cursorOffset: number,
): OracleTriggerHostTable | undefined {
  if (cursorOffset <= 0) return undefined;
  const tokens = tokenize(source, 0, source.length, ENGINE);
  // Cap at cursor — only triggers whose ON clause is BEFORE the cursor bind
  // it to a host. We still walk the whole array up to that point so the
  // "latest one wins" rule below can pick the most recent hit.
  const visible: Token[] = [];
  for (const t of tokens) {
    if (t.start >= cursorOffset) break;
    visible.push(t);
  }

  let latest: OracleTriggerHostTable | undefined;

  for (let i = 0; i < visible.length; i += 1) {
    if (upperOf(visible[i]) !== 'CREATE') continue;
    // Skip optional `OR REPLACE`.
    let j = i + 1;
    if (upperOf(visible[j]) === 'OR' && upperOf(visible[j + 1]) === 'REPLACE') {
      j += 2;
    }
    if (upperOf(visible[j]) !== 'TRIGGER') continue;

    // Walk forward from after TRIGGER looking for the first `ON` at depth 0
    // (paren depth — defensive, the header has no parens but bounds the
    // search). Cap the search at a reasonable lookahead so a malformed
    // CREATE TRIGGER doesn't consume the whole buffer.
    let depth = 0;
    const MAX_HEADER_TOKENS = 60;
    const headerEnd = Math.min(visible.length, j + 1 + MAX_HEADER_TOKENS);
    let onIdx = -1;
    for (let k = j + 1; k < headerEnd; k += 1) {
      const t = visible[k]!;
      if (t.type === 'lparen') depth += 1;
      else if (t.type === 'rparen') depth = Math.max(0, depth - 1);
      else if (depth === 0 && upperOf(t) === 'ON') {
        onIdx = k;
        break;
      } else if (depth === 0 && t.type === 'semicolon') {
        break;
      }
    }
    if (onIdx === -1) continue;

    // After ON, we expect `<ident>` or `<ident>.<ident>`. The trigger
    // header is in scope iff the cursor is past the `ON … <table>` segment;
    // if the cursor sits BEFORE the ON, we shouldn't bind. We already
    // capped `visible` at cursor — so reaching this point means ON came
    // before the cursor. We still need the host-table identifier(s) to be
    // before the cursor too.
    const part1 = visible[onIdx + 1];
    if (!part1 || part1.type !== 'ident') continue;
    const maybeDot = visible[onIdx + 2];
    if (maybeDot && maybeDot.type === 'dot') {
      const part2 = visible[onIdx + 3];
      if (part2 && part2.type === 'ident') {
        latest = {
          name: part2.text.toLowerCase(),
          schema: part1.text.toLowerCase(),
        };
        i = onIdx + 3;
        continue;
      }
    }
    latest = { name: part1.text.toLowerCase(), schema: undefined };
    i = onIdx + 1;
  }

  return latest;
}
